import ajax from '../../common/services/httpService';
import { localeData } from '../../common/services/localeService';
import { applyCleanerRoomsToFilter, hasServikNameFilter } from '../../common/services/filterService';
import chartService from './performanceChartService';
import cacheService from '../../common/services/cacheService';

// Track current filter to prevent stale API calls
let currentFilterState = { company: null, site: null };

/**
 * Update the current filter state (called from store when filter changes)
 */
export function updateCurrentFilterState(filter) {
    if (filter && filter.company && filter.site) {
        currentFilterState = {
            company: filter.company,
            site: filter.site
        };
    }
}

/**
 * Get the current filter state (for use in service functions)
 */
function getCurrentFilterState() {
    return { ...currentFilterState };
}

export {
    getOverviewData,
    getOverviewSums,
    getAnalyticsData,
    getRoomDataByHour,
    getRoomById,
    getTopIssues,
    getAnalyticsSums,
    getRoomCleanerArrivals,
    getRoomExposures,
    getRoomsCounterCallsSum,
    getRoomsCleandexCallsSum,
    getRoomsManualCallsSum,
    getRoomsUnnecessaryArrivals,
    getOverviewUnnecessaryArrivals,
    isUnnecessaryArrivalsShown,
    isMetricShown,
    getUnnecessaryArrivalsShareText,
    getCleanerActivity,
    getCleanerHistory,
    emptyCleanerActivitySummary,
    clearPerformanceCache
};

/** Whole minutes, same as site-level response_time_SLA KPI. */
function roundResponseTimeMinutes(minutes) {
    if (minutes == null || Number.isNaN(Number(minutes))) return minutes
    const value = Number(minutes)
    if (value < 1) return Math.ceil(value)
    return Math.round(value)
}

export default {
    trendInfoForBarItems,
    getSiteArrivalsByHour,
    getAnalysisReport
};

/**
 * Calculate previous period dates based on current period dates
 * Ensures previous period has the exact same range as current period
 * @param {{ timeFrom?: String; timeTo?: String; }} time 
 * @returns {{ timeFrom?: String; timeTo?: String; }} Previous period dates
 */
function _calculatePreviousPeriodDates(time) {
    if (!time || !time.timeFrom || !time.timeTo) {
        return null;
    }
    
    // Parse dates as UTC to avoid timezone issues
    // time.timeFrom is in format "YYYY-MM-DD"
    const [yearFrom, monthFrom, dayFrom] = time.timeFrom.split('-').map(Number);
    const [yearTo, monthTo, dayTo] = time.timeTo.split('-').map(Number);
    
    const timeFrom = new Date(Date.UTC(yearFrom, monthFrom - 1, dayFrom, 0, 0, 0));
    const timeTo = new Date(Date.UTC(yearTo, monthTo - 1, dayTo, 0, 0, 0));
    
    // Calculate the number of days in the current period (difference only, not inclusive)
    // e.g., Oct 27 to Nov 2: difference is 6 days (Oct 27, 28, 29, 30, 31, Nov 1, Nov 2 = 7 days inclusive)
    const daysDiff = Math.floor((timeTo - timeFrom) / (1000 * 60 * 60 * 24));
    
    // Previous period should end one day before current period starts
    // If current starts Oct 27, previous ends Oct 26
    const previousTimeTo = new Date(timeFrom);
    previousTimeTo.setUTCDate(previousTimeTo.getUTCDate() - 1);
    
    // Previous period should start the same number of days before previousTimeTo
    // If current is Oct 27 to Nov 2 (6 days difference = 7 days inclusive),
    // previous should be Oct 20 to Oct 26 (6 days difference = 7 days inclusive)
    // So from Oct 26, go back 6 days to get Oct 20
    const previousTimeFrom = new Date(previousTimeTo);
    previousTimeFrom.setUTCDate(previousTimeFrom.getUTCDate() - daysDiff);
    
    return {
        timeFrom: previousTimeFrom.toISOString().slice(0, 10),
        timeTo: previousTimeTo.toISOString().slice(0, 10)
    };
}

/**
 * Calculate trend by comparing current period to previous equivalent period
 * @param {Object} overviewData Current period overview data
 * @param {Object} filter Filter object containing time range
 * @returns {Promise<Object<string, { text: String; upDown: 'up' | 'down' }>>} Trend info
 */
async function trendInfoForBarItems(overviewData, filter) {
    const trendArrowNames = ['avg_score','exposures', 'complaints', 'maintenanceComplaint', 'response_time_SLA', 'time_saved'];
    
    // If no overviewData, return empty trend info
    if (!overviewData) {
        return trendArrowNames.reduce((acc, name) => {
            acc[name] = { upDown: 'up', text: '0.0%' };
            return acc;
        }, {});
    }
    
    // If no filter or time range provided, fall back to original calculation
    if (!filter || !filter.time || !filter.time.timeFrom || !filter.time.timeTo) {
        return _calculateTrendFromData(overviewData, trendArrowNames);
    }
    
    // Calculate previous period dates
    const previousPeriodDates = _calculatePreviousPeriodDates(filter.time);
    if (!previousPeriodDates) {
        return _calculateTrendFromData(overviewData, trendArrowNames);
    }
    
    // Fetch previous period data
    // Ensure we use the current filter's company/site, not a stale one
    const currentFilterState = getCurrentFilterState();
    
    // Remove servikName from filter (servikNames are site-specific and shouldn't persist across company/site changes)
    // Also ensure we use current company/site
    const { servikName, ...filterWithoutServikName } = filter;
    const previousPeriodFilter = {
        ...filterWithoutServikName,
        // Override with current company/site to prevent stale API calls
        company: currentFilterState.company || filter.company,
        site: currentFilterState.site || filter.site,
        time: previousPeriodDates
    };
    
    // Validate filter matches current state before making API call
    if (currentFilterState.company !== null && previousPeriodFilter.company !== currentFilterState.company) {
        previousPeriodFilter.company = currentFilterState.company;
        previousPeriodFilter.site = currentFilterState.site;
    }
    
    let previousOverviewData;
    try {
        previousOverviewData = await getOverviewData(previousPeriodFilter);
    } catch (error) {
        return _calculateTrendFromData(overviewData, trendArrowNames);
    }
    
    if (!previousOverviewData) {
        return _calculateTrendFromData(overviewData, trendArrowNames);
    }
    
    // Calculate sums for both periods
    const currentSums = getOverviewSums(overviewData);
    const previousSums = getOverviewSums(previousOverviewData);
    
    // Ensure sums are valid objects
    if (!currentSums || !previousSums) {
        return _calculateTrendFromData(overviewData, trendArrowNames);
    }
    
    // Use helper function to calculate trend from sums
    const trendInfo = _calculateTrendFromSums(currentSums, previousSums, overviewData, trendArrowNames);
    _attachComplaintsVisitsNote(trendInfo, _isValueUp(currentSums.visits, previousSums.visits));
    return trendInfo;
}

/**
 * @param {number|null|undefined} currentValue
 * @param {number|null|undefined} previousValue
 * @returns {boolean}
 */
function _isValueUp(currentValue, previousValue) {
    if (currentValue === null || currentValue === undefined ||
        previousValue === null || previousValue === undefined ||
        isNaN(currentValue) || isNaN(previousValue)) {
        return false;
    }
    return Number(currentValue) > Number(previousValue);
}

/**
 * When complaints are up and visits are also up, attach a clarifying note so users
 * do not assume quality dropped solely because complaint count rose.
 * @param {Object<string, { text: String; upDown: 'up' | 'down'; noteKey?: String }>} trendInfo
 * @param {boolean} visitsUp
 */
function _attachComplaintsVisitsNote(trendInfo, visitsUp) {
    if (!trendInfo || !trendInfo.complaints) return;
    if (trendInfo.complaints.upDown === 'up' && visitsUp) {
        trendInfo.complaints.noteKey = 'visits_also_went_up';
    }
}

/**
 * Calculate trend from sums (current vs previous period)
 * @param {Object} currentSums Current period sums
 * @param {Object} previousSums Previous period sums
 * @param {Object} overviewData Overview data (for fallback)
 * @param {string[]} trendArrowNames Array of metric names
 * @returns {Object<string, { text: String; upDown: 'up' | 'down'; noteKey?: String }>} Trend info
 */
function _calculateTrendFromSums(currentSums, previousSums, overviewData, trendArrowNames) {
    /**@type {Object<string, { text: String; upDown: 'up' | 'down'; noteKey?: String }>} */
    const trendInfo = trendArrowNames.reduce((acc, name) => {
        if (!overviewData || !overviewData[name] || !overviewData[name].length) return acc;
        
        // Check if this metric exists in overviewSums (some metrics like maintenanceComplaint might not)
        if (!(name in currentSums) || !(name in previousSums)) {
            // Fall back to original calculation for this metric
            if (overviewData) {
                const fallbackTrend = _calculateTrendFromData(overviewData, [name]);
                if (fallbackTrend[name]) {
                    acc[name] = fallbackTrend[name];
                }
            } else {
                // If no overviewData, set default values
                acc[name] = { upDown: 'up', text: '0.0%' };
            }
            return acc;
        }
        
        acc[name] = {};
        
        // Get current and previous period values
        const currentValue = currentSums[name];
        const previousValue = previousSums[name];
        
        // Check if values are valid
        if (currentValue === null || currentValue === undefined || 
            previousValue === null || previousValue === undefined ||
            isNaN(currentValue) || isNaN(previousValue)) {
            acc[name].upDown = 'up';
            acc[name].text = '0.0%';
            return acc;
        }
        
        // Calculate percentage change
        const diff = currentValue - previousValue;
        const denominator = Math.max(1, Math.abs(previousValue));
        const percentage = (Math.abs(diff) / denominator * 100);
        
        // Ensure percentage is finite
        if (!isFinite(percentage)) {
            acc[name].upDown = 'up';
            acc[name].text = '0.0%';
            return acc;
        }
        
        // Determine direction: for some metrics, 'up' means improvement (like avg_score), 
        // for others, 'up' means increase (like complaints)
        // Based on the original logic, we'll use: if current > previous, it's 'up'
        // The color mapping in trendArrowColors will determine if that's good or bad
        acc[name].upDown = (currentValue > previousValue) ? 'up' : 'down';
        acc[name].text = `${percentage.toFixed(1)}%`;
        
        return acc;
    }, {});
    
    return trendInfo;
}

/**
 * Fallback: Calculate trend from data using linear regression (original method)
 * @param {Object} overviewData Overview data
 * @param {string[]} trendArrowNames Array of metric names
 * @returns {Object<string, { text: String; upDown: 'up' | 'down' }>} Trend info
 */
function _calculateTrendFromData(overviewData, trendArrowNames) {
    // If no overviewData, return empty trend info
    if (!overviewData) {
        return trendArrowNames.reduce((acc, name) => {
            acc[name] = { upDown: 'up', text: '0.0%' };
            return acc;
        }, {});
    }
    
    /**@type {Object<string, { text: String; upDown: 'up' | 'down' }>} */
    const trendInfo = trendArrowNames.reduce((acc, name) => {
        if (!overviewData[name] || !overviewData[name].length) return acc;
        acc[name] = {};
        // Exclude missing values to avoid skewing the regression toward zero
        const values = overviewData[name]
            .map(a => a.count)
            .filter(count => count !== null && count !== undefined)
            .map(count => +count)
            .filter(val => !isNaN(val));
        
        // Need at least 2 points to calculate a trend
        if (values.length < 2) {
            acc[name].upDown = 'up';
            acc[name].text = '0.0%';
            return acc;
        }
        
        const { start, end } = chartService.trendStartEnd(values);
        
        // Check for NaN or invalid results
        if (isNaN(start) || isNaN(end) || !isFinite(start) || !isFinite(end)) {
            acc[name].upDown = 'up';
            acc[name].text = '0.0%';
            return acc;
        }
        
        const diff = end - start;
        // Guard against tiny or zero denominators inflating percentages
        const denominator = Math.max(1, Math.abs(start));
        const percentage = (Math.abs(diff) / denominator * 100);
        
        // Ensure percentage is finite
        if (!isFinite(percentage)) {
            acc[name].upDown = 'up';
            acc[name].text = '0.0%';
            return acc;
        }
        
        acc[name].upDown = (start < end) ? 'up' : 'down';
        acc[name].text = `${percentage.toFixed(1)}%`;
        return acc;
    }, {});

    // Fallback path: infer visits direction from the visits series in the current range
    let visitsUp = false;
    if (overviewData.visits && overviewData.visits.length >= 2) {
        const visitValues = overviewData.visits
            .map((a) => a.count)
            .filter((count) => count !== null && count !== undefined)
            .map((count) => +count)
            .filter((val) => !isNaN(val));
        if (visitValues.length >= 2) {
            const { start, end } = chartService.trendStartEnd(visitValues);
            visitsUp = !isNaN(start) && !isNaN(end) && isFinite(start) && isFinite(end) && start < end;
        }
    }
    _attachComplaintsVisitsNote(trendInfo, visitsUp);

    return trendInfo;
}

/**
 * @param {Number | String} roomId
 * @param {{ timeFrom?: String; timeTo?: String; }} query
*/
async function getRoomCleanerArrivals(roomId, filter) {
    /**@type {Array<string>[]} - matrix of utc date string*/
    const cleanerArrivalData = await ajax.get(`stats/room/cleaner-arrival/${roomId}`, null, filter);
    cleanerArrivalData.forEach((item) => {
        item.forEach((arrival) => {
            if (arrival.time) {
                const date = new Date(arrival.time)
                arrival.time = new Date(date.toISOString().slice(0, -1))
            }
        })
    })
    return cleanerArrivalData;
}

/**
 * @param {Number | String} roomId
 * @param {{companyId:String, siteId:Number, timeFrom?: String; timeTo?: String; }} query
*/
async function getRoomExposures(roomId, filter) {
    /**@type {Array<string>[]} - matrix of utc date string*/
    const roomExposuresData = await ajax.get(`stats/room/room-exposures/${roomId}`, null, filter);
    roomExposuresData.forEach((item) => {
        item.forEach((exposure) => {
            if (exposure.endTime) {
                const date = new Date(exposure.endTime)
                exposure.endTime = new Date(date.toISOString().slice(0, -1))
            }
            if (exposure.startTime) {
                exposure.startTime = new Date(exposure.startTime)
            }
        })
    })
    return roomExposuresData
}

/** Empty site-level summary, matching the backend shape, used as a fallback. */
function emptyCleanerActivitySummary() {
    return {
        totalCleaners: 0,
        activeCleaners: 0,
        closedComplaints: 0,
        totalWorkMinutes: 0,
        totalBreakMinutes: 0,
        avgResponseMinutes: null,
        openComplaints: 0,
        slaBreaches: 0,
    };
}

/**
 * Per-cleaner daily activity for a site (clock in/out, breaks, complaints) plus a
 * site-level summary for the KPI cards. Date fields are returned as UTC ISO strings and parsed as
 * Date objects for timeline placement. Display formatting uses the site shift GMT in the UI
 * (see CleanerActivity / CleanerActivityTimeline).
 * @param {Number | String} siteId
 * @param {{ time?: { timeFrom?: String; timeTo?: String; }, maxSla?: Number | String | null, shiftId?: Number | String | null, shiftStart?: String | null, shiftEnd?: String | null }} filter
 * @param {{ silent?: Boolean }} [options]
 * @returns {Promise<{ summary: Object, cleaners: Array<Object> }>}
 */
async function getCleanerActivity(siteId, filter, options = {}) {
    const time = (filter && filter.time) || {};
    const maxSla = filter && filter.maxSla != null ? filter.maxSla : undefined;
    const shiftId = filter && filter.shiftId != null ? filter.shiftId : undefined;
    const shiftStart = filter && filter.shiftStart ? filter.shiftStart : undefined;
    const shiftEnd = filter && filter.shiftEnd ? filter.shiftEnd : undefined;
    const response = await ajax.get(`employee/getCleanerActivity/${siteId}`, null, {
        timeFrom: time.timeFrom,
        timeTo: time.timeTo,
        maxSla,
        shiftId,
        shiftStart,
        shiftEnd,
    }, options);

    const summary = (response && response.summary) || emptyCleanerActivitySummary();
    const cleaners = response && Array.isArray(response.cleaners) ? response.cleaners : [];
    if (!cleaners.length) return { summary, cleaners: [] };

    const toDate = (isoString) => {
        if (!isoString) return null;
        const date = new Date(isoString);
        return Number.isNaN(date.getTime()) ? null : date;
    };

    cleaners.forEach((cleaner) => {
        const sessions = Array.isArray(cleaner.sessions) ? cleaner.sessions : [];
        const complaints = Array.isArray(cleaner.complaints) ? cleaner.complaints : [];
        cleaner.sessions = sessions;
        cleaner.complaints = complaints;
        cleaner.roomIds = Array.isArray(cleaner.roomIds)
            ? cleaner.roomIds.map(Number).filter(Number.isFinite)
            : [];
        cleaner.workingAreaIds = Array.isArray(cleaner.workingAreaIds)
            ? cleaner.workingAreaIds.map(Number).filter(Number.isFinite)
            : [];

        sessions.forEach((session) => {
            session.clockIn = toDate(session.clockIn);
            session.clockOut = toDate(session.clockOut);
        });
        complaints.forEach((complaint) => {
            complaint.openTime = toDate(complaint.openTime);
            complaint.closeTime = toDate(complaint.closeTime);
            if (Array.isArray(complaint.additionals)) {
                complaint.additionals.forEach((additional) => {
                    additional.time = toDate(additional.time);
                });
            }
        });
    });

    return { summary, cleaners };
}

/**
 * Attendance history for a site over a company-local date range: days worked, hours, breaks,
 * late and missed days per person. Timestamps stay UTC ISO strings; the UI formats them in the
 * company timezone returned as `range.gmt`.
 * @param {Number | String} siteId
 * @param {{ dateFrom: String, dateTo: String }} range - `YYYY-MM-DD`, company-local
 * @returns {Promise<{ range: Object, summary: Object, people: Array<Object> }>}
 */
async function getCleanerHistory(siteId, { dateFrom, dateTo }) {
    return ajax.get(`employee/getCleanerHistory/${siteId}`, null, { dateFrom, dateTo });
}

/**
 * Per-room arrivals for the analytics export: `cleaner`/`supervisor` hold per-hour counts from the stored
 * hour_map (1:1 with the panel hour table), and `cleanerTotals`/`supervisorTotals` hold the shift-wide
 * per-room totals (1:1 with the panel KPI and the room table).
 * @param {Number | String} siteId
 * @param {{ time?: { timeFrom?: String; timeTo?: String }; rooms?: (Number|String)[]; building?: String; floor?: String[]; gender?: String[]; shift?: (Number|String)[]; days?: (Number|String)[]; }} filter
 * @returns {Promise<{
 * cleaner: Object<string, Object<number, number>>;
 * supervisor: Object<string, Object<number, number>>;
 * cleanerTotals: Object<string, number>;
 * supervisorTotals: Object<string, number>;
 * }>}
 */
async function getSiteArrivalsByHour(siteId, filter = {}) {
    const arrivals = await ajax.get(`stats/room/site-arrivals-by-hour/${siteId}`, null, filter);
    return arrivals;
}

/**@param { String | Number } id * @param {Object} filter*/
async function getRoomDataByHour(id, filter) {
    const dataByHour = await ajax.get(`stats/room/hourData/${id}`, null, filter);
    return dataByHour;
}

async function getRoomById(id) {
    const room = await ajax.get(`room/${id}`);
    return room;
}

async function getTopIssues(filter) {
    filter.language = localeData.language
    const issues = await ajax.get('suggestions/topSuggestions', null, filter);
    for (const field in issues) {
        issues[field] = issues[field].map(complaint => {
            return {
                ...complaint,
                hour: complaint.hour,
            }
        })
    }
    return issues;
}

async function getAnalyticsData(filter) {
    const currentState = getCurrentFilterState();
    
    // Validate filter matches current filter state to prevent stale API calls
    if (currentState.company !== null && filter.company !== currentState.company) {
        return null;
    }
    if (currentState.site !== null && filter.site !== currentState.site) {
        return null;
    }
    
    filter.language = localeData.language
    const analyticsData = await ajax.get('stats/performance/analytics', null, applyCleanerRoomsToFilter(filter));
    if (!analyticsData) return analyticsData;
    const localeHourData = analyticsData.hourData.map(_sortLocaleHours).sort((a, b) => a.time - b.time);
    analyticsData.hourData = localeHourData;
    return analyticsData;
}

function _sortLocaleHours(hourItem) {
    const initialDate = new Date();
    initialDate.setHours(hourItem.time)
    const localHour = initialDate.getHours();
    hourItem.time = localHour;
    return hourItem;
}

async function getOverviewData(filter) {
    const currentState = getCurrentFilterState();
    
    // Validate filter matches current filter state to prevent stale API calls
    if (currentState.company !== null && filter.company !== currentState.company) {
        return null;
    }
    if (currentState.site !== null && filter.site !== currentState.site) {
        return null;
    }
    
    const endpoint = 'stats/performance/overview';
    
    // Caching temporarily disabled while we validate live data freshness
    return await ajax.get(endpoint, null, applyCleanerRoomsToFilter(filter));
}

function _emptyOverviewSums() {
    return {
        visits: 0,
        exposures: 0,
        complaints: 0,
        response_time_SLA: 0,
        avg_score: 0,
        time_saved: 0,
        est_ROI: 0,
        complaints_vs_visits: 0,
        cleaner_arrivals: 0,
        supervisor_arrival: 0,
        unnecessary_arrivals: null,
        unnecessary_arrivals_checked: null,
        isAmended: 0,
    }
}

function getOverviewSums(overviewData) {
    // Validate input
    if (!overviewData || !overviewData.visits || !Array.isArray(overviewData.visits)) {
        return _emptyOverviewSums()
    }

    let sum = _emptyOverviewSums()
    let exposuresCount = 0;

    //all arrays have the same length
    overviewData.visits.forEach((item, index) => {
        if (item && item.isAmended) sum.isAmended = 1
        if (item && item.count !== null && item.count !== undefined) {
            sum.visits += item.count || 0;
        }
        
        // Safely access each array with defensive checks
        if (overviewData.complaints && overviewData.complaints[index] && overviewData.complaints[index].count !== null && overviewData.complaints[index].count !== undefined) {
            sum.complaints += overviewData.complaints[index].count || 0;
        }
        
        if (overviewData.arrivals && overviewData.arrivals[index] && overviewData.arrivals[index].count !== null && overviewData.arrivals[index].count !== undefined) {
            sum.cleaner_arrivals += overviewData.arrivals[index].count || 0;
        }
        
        if (overviewData.response_time_SLA && overviewData.response_time_SLA[index] && overviewData.response_time_SLA[index].count !== null && overviewData.response_time_SLA[index].count !== undefined) {
            sum.response_time_SLA += overviewData.response_time_SLA[index].count || 0;
        }
        
        if (overviewData.avg_score && overviewData.avg_score[index] && overviewData.avg_score[index].count !== null && overviewData.avg_score[index].count !== undefined) {
            sum.avg_score += overviewData.avg_score[index].count || 0;
        }
        
        if (overviewData.exposures && overviewData.exposures[index] && overviewData.exposures[index].count !== null && overviewData.exposures[index].count !== undefined) {
            sum.exposures += overviewData.exposures[index].count || 0;
            if (overviewData.exposures[index].count) exposuresCount++;
        }
        
        if (overviewData.time_saved && overviewData.time_saved[index] && overviewData.time_saved[index].count !== null && overviewData.time_saved[index].count !== undefined) {
            sum.time_saved += overviewData.time_saved[index].count || 0;
        }
        
        if (overviewData.supervisor_arrival && overviewData.supervisor_arrival[index] && overviewData.supervisor_arrival[index].count !== null && overviewData.supervisor_arrival[index].count !== undefined) {
            sum.supervisor_arrival += overviewData.supervisor_arrival[index].count || 0;
        }
        
        if (overviewData.est_ROI && overviewData.est_ROI[index] && overviewData.est_ROI[index].count !== null && overviewData.est_ROI[index].count !== undefined) {
            sum.est_ROI += overviewData.est_ROI[index].count || 0;
        }

        // Stays null while no period has it computed, so "not computed" is not shown as 0
        const unnecessaryArrivalsItem = overviewData.unnecessary_arrivals && overviewData.unnecessary_arrivals[index];
        if (unnecessaryArrivalsItem && unnecessaryArrivalsItem.count !== null && unnecessaryArrivalsItem.count !== undefined) {
            sum.unnecessary_arrivals = (sum.unnecessary_arrivals || 0) + unnecessaryArrivalsItem.count;
            sum.unnecessary_arrivals_checked = (sum.unnecessary_arrivals_checked || 0) + (unnecessaryArrivalsItem.checkedArrivals || 0);
        }
    })
    //avg is for each one, so it should be devided by total
    const { response_time_SLA, avg_score } = overviewData || {};
    if (response_time_SLA && Array.isArray(response_time_SLA)) {
        const lenOfDaysWithResponseTime = _getLengthOfDaysWithData(response_time_SLA);
        if (lenOfDaysWithResponseTime > 0) {
            sum.response_time_SLA = roundResponseTimeMinutes(
                sum.response_time_SLA / lenOfDaysWithResponseTime,
            );
        } else {
            sum.response_time_SLA = 0;
        }
    } else {
        sum.response_time_SLA = 0;
    }

    if (avg_score && Array.isArray(avg_score)) {
        const lenOfDaysWithScore = _getLengthOfDaysWithData(avg_score);
        if (lenOfDaysWithScore > 0) {
            sum.avg_score = sum.avg_score / lenOfDaysWithScore;
            sum.avg_score = Math.round(sum.avg_score);
        } else {
            sum.avg_score = 0;
        }
    } else {
        sum.avg_score = 0;
    }

    if (exposuresCount > 0) {
        sum.exposures = Math.round((sum.exposures / exposuresCount));
    } else {
        sum.exposures = 0;
    }

    sum.visits = Math.floor(sum.visits);

    // sum.complaints_vs_visits = sum.complaints_vs_visits / (overviewData.complaints_vs_visits.length - complaintVSvisitsInfinityCount);
    // sum.complaints_vs_visits = sum.complaints_vs_visits.toFixed(3);

    // Customer success team asked to change how we calculate the complaints_vs_visits
    if (sum.visits > 0) {
        sum.complaints_vs_visits = ((sum.complaints / sum.visits) * 100).toFixed(3);
    } else {
        sum.complaints_vs_visits = '0.000';
    }
    return sum;
}

function getAnalyticsSums(hourData) {
    const sums = hourData.reduce((acc, item) => {
        const complaintSum = item.complaints;
        if (item.isAmended) { acc.isAmended = 1 }
        (acc.visits || acc.visits === 0) ? acc.visits += item.visits : acc.visits = item.visits;
        (acc.arrivals || acc.arrivals === 0) ? acc.arrivals += item.arrivals : acc.arrivals = item.arrivals;
        (acc.maintenanceArrivals || acc.maintenanceArrivals === 0) ? acc.maintenanceArrivals += item.maintenanceArrivals : acc.maintenanceArrivals = item.maintenanceArrivals;
        (acc.maintenanceComplaint || acc.maintenanceComplaint === 0) ? acc.maintenanceComplaint += item.maintenanceComplaint : acc.maintenanceComplaint = item.maintenanceComplaint;
        (acc.complaints || acc.complaints === 0) ? acc.complaints += complaintSum : acc.complaints = complaintSum;
        return acc;
    }, {});
    return sums;
}

function _sumRoomField(rooms, field) {
    let total = 0
    rooms.forEach(room => { total += room[field] })
    return total
}

function getRoomsCounterCallsSum(rooms) {
    return _sumRoomField(rooms, 'counterCalls')
}

function getRoomsCleandexCallsSum(rooms) {
    return _sumRoomField(rooms, 'cleandexCalls')
}

function getRoomsManualCallsSum(rooms) {
    return _sumRoomField(rooms, 'manualCalls')
}

/** The overview total, or null while no period in the range has the column computed. */
function getOverviewUnnecessaryArrivals(overviewSums) {
    if (!overviewSums || overviewSums.unnecessary_arrivals === null || overviewSums.unnecessary_arrivals === undefined) return null
    return {
        count: overviewSums.unnecessary_arrivals,
        checkedArrivals: overviewSums.unnecessary_arrivals_checked,
    }
}

/**
 * @param {{ unnecessaryArrivals: number | null; checkedArrivals: number | null; }[]} rooms
 * @returns {{ count: number; checkedArrivals: number; } | null} null while no room has it computed
 */
function getRoomsUnnecessaryArrivals(rooms) {
    if (!rooms) return null
    const computedRooms = rooms.filter(room => room.unnecessaryArrivals !== null && room.unnecessaryArrivals !== undefined)
    if (!computedRooms.length) return null
    return computedRooms.reduce((acc, room) => {
        acc.count += Number(room.unnecessaryArrivals)
        acc.checkedArrivals += Number(room.checkedArrivals) || 0
        return acc
    }, { count: 0, checkedArrivals: 0 })
}

/**
 * Unnecessary arrivals are judged out of the grouped arrivals, so they are meaningless when a complaint type
 * filter narrows the complaints, or when no arrival was judged.
 * @param {{ count: number; checkedArrivals: number; } | null} unnecessaryArrivals
 * @param {*} servikName - the selected complaint type filter
 */
function isUnnecessaryArrivalsShown(unnecessaryArrivals, servikName) {
    return !!unnecessaryArrivals && unnecessaryArrivals.checkedArrivals > 0 && !hasServikNameFilter(servikName)
}

/** Every other metric shows when its sum is set. No-call arrivals also show at 0, and hide under a complaint filter. */
function isMetricShown(metricName, fallbackShown, unnecessaryArrivals, servikName) {
    if (metricName === 'unnecessary_arrivals') return isUnnecessaryArrivalsShown(unnecessaryArrivals, servikName)
    return !!fallbackShown
}

/** @param {{ count: number; checkedArrivals: number; }} unnecessaryArrivals */
function getUnnecessaryArrivalsShare(unnecessaryArrivals) {
    if (unnecessaryArrivals.count > unnecessaryArrivals.checkedArrivals) {
        throw new Error(`Unnecessary arrivals ${unnecessaryArrivals.count} exceed the ${unnecessaryArrivals.checkedArrivals} arrivals they were judged from`)
    }
    return Math.round((unnecessaryArrivals.count / unnecessaryArrivals.checkedArrivals) * 100)
}

/** "(8%)", or "8% of arrivals" when the label is passed. */
function getUnnecessaryArrivalsShareText(unnecessaryArrivals, ofArrivalsLabel) {
    const share = getUnnecessaryArrivalsShare(unnecessaryArrivals)
    return ofArrivalsLabel ? `${share}% ${ofArrivalsLabel}` : `(${share}%)`
}

function _getLengthOfDaysWithData(data) {
    return data.filter(item => item.count !== null).length
}

async function getAnalysisReport(filter) {
    const report = await ajax.get('stats/performance/analysis-report', null, filter)
    return report
}

/**
 * Clear cache for performance overview endpoint
 * @param {Object} filter - Optional filter to clear specific cache entry, or null to clear all overview cache
 */
function clearPerformanceCache(filter = null) {
    if (filter) {
        // Clear specific cache entry for overview
        cacheService.clearCache('stats/performance/overview', filter);
    } else {
        // Clear all overview cache
        cacheService.clearCache();
    }
}